import React from 'react';
import { Target, Eye, Heart, Users, Award } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageHero from '../components/PageHero';
import SectionHead from '../components/SectionHead';
import CTASection from '../components/CTASection';
import { Reveal, spotlight, SectionRule } from '../components/motion';
import { partners } from '../data/mock';

const values = [
  { icon: Target, title: 'Customer focus', description: 'We start from your needs and tailor solutions that deliver real value.' },
  { icon: Award, title: 'Excellence', description: 'The highest standards in everything, from delivery to support.' },
  { icon: Heart, title: 'Integrity', description: 'Transparency and honesty in every interaction build lasting trust.' },
  { icon: Users, title: 'Collaboration', description: 'We work as an extension of your team and share in your success.' },
];

const timeline = [
  { year: '2008', title: 'Company founded', description: 'enseQure Incorporation was established in Harare, Zimbabwe.' },
  { year: '2012', title: 'Regional expansion', description: 'Expanded operations to South Africa and other African markets.' },
  { year: '2017', title: 'AWS partnership', description: 'Awarded AWS ASEAN Consulting Partner of the Year.' },
  { year: '2020', title: 'European presence', description: 'Opened offices in Prague, Czechia to serve European clients.' },
  { year: '2024', title: 'AI & innovation', description: 'Launched AI services and WhatsApp ChatBot development.' },
  { year: '2025', title: 'Continued growth', description: 'Serving 500+ enterprise clients across three continents.' },
];

const AboutPage = () => (
  <>
    <Navbar />
    <main>
      <PageHero index="02" eyebrow="About us" title={['Built on trust,', '{{proven}} across continents.']}>
        For over fifteen years we have helped organisations connect, protect and scale their technology.
      </PageHero>

      <SectionRule index="01" label="Mission & vision" />
      <section className="py-8">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-5">
          {[{ icon: Target, t: 'Our mission', p: 'To enable businesses to be connected anywhere in the world, offering quality services that are prompt and reliable. We help organizations achieve their business objectives through robust network and security infrastructure, advanced storage solutions, efficient virtualization, strategic cloud adoption, and comprehensive managed services.' },
            { icon: Eye, t: 'Our vision', p: 'To be a leading provider of IT infrastructure services and cloud solutions, empowering organizations with cutting-edge technology and expertise to maximize their potential and ensure uninterrupted business operations. We guide businesses through the digital age seamlessly.' }].map((c, i) => (
            <Reveal key={c.t} delay={i * 120}>
              <article onMouseMove={spotlight} className="spot p-10 h-full">
                <c.icon className="w-9 h-9 text-[var(--red)]" strokeWidth={1.3} />
                <h2 className="font-display text-5xl mt-10 mb-4">{c.t}</h2>
                <p className="text-[var(--muted)] leading-relaxed text-lg">{c.p}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <SectionRule index="02" label="Values" />
      <section className="py-8 bg-[var(--surface)]">
        <div className="max-w-6xl mx-auto px-6">
          <SectionHead index="01" label="Values" title="What we {{stand for.}}" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--line-strong)] border border-[var(--line-strong)] rounded-3xl overflow-hidden">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 90} className="bg-[var(--ink)] p-8 hover:bg-[var(--surface-2)] transition-colors">
                <v.icon className="w-7 h-7 text-[var(--red)]" strokeWidth={1.4} />
                <h3 className="font-display text-3xl mt-8 mb-3">{v.title}</h3>
                <p className="text-sm text-[var(--muted)] leading-relaxed">{v.description}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <SectionRule index="03" label="Our journey" />
      <section className="py-8">
        <div className="max-w-6xl mx-auto px-6">
          <SectionHead index="02" label="Our journey" title="Seventeen years, {{one direction.}}" />
          <ol className="relative border-l border-[var(--line-strong)] ml-2">
            {timeline.map((t, i) => (
              <Reveal as="li" key={t.year} delay={i * 60} className="relative pl-10 pb-14 last:pb-0 grid md:grid-cols-[10rem_1fr] gap-2 md:gap-10">
                <span className="absolute -left-[5px] top-3 w-2.5 h-2.5 rounded-full bg-[var(--red)] shadow-[0_0_0_6px_var(--red-soft)]" />
                <span className="font-display text-6xl text-[var(--red)] leading-none">{t.year}</span>
                <div className="pt-2"><h3 className="text-xl font-semibold mb-1">{t.title}</h3><p className="text-[var(--muted)]">{t.description}</p></div>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <SectionRule index="04" label="Partners" />
      <section className="py-7 bg-[var(--surface)]">
        <div className="max-w-6xl mx-auto px-6">
          <SectionHead index="03" label="Partners" title="Certified with the {{best.}}" />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
            {partners.map((p, i) => (
              <Reveal key={p.name} delay={i * 70}>
                <div className="rounded-2xl border border-[var(--line)] bg-[var(--ink)] p-8 h-full flex flex-col items-center text-center hover:border-[var(--red)] transition-colors">
                  <img src={p.logo} alt={p.name} className="h-10 w-auto partner-logo opacity-80" />
                  <p className="mt-6 text-sm text-[var(--muted)]">{p.badge}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <SectionRule index="05" label="Contact" />
      <CTASection title="Work with people who care." />
    </main>
    <Footer />
  </>
);

export default AboutPage;
